/* lib/entities.js — THE UNIVERSAL COMPONENT REGISTRY (Cabinet Technician V2).
 *
 * Every physical object the platform knows about becomes ONE kind of record —
 * a LibEntity — no matter which library it used to live in. The registry is
 * the MASTER for editing; the historical blob arrays (colors, groups, handles,
 * gola, pmat, worktops, equipment.*, …) become a deterministic PROJECTION of
 * it that is rebuilt on every write.
 *
 * WHY A PROJECTION AND NOT A REPLACEMENT — the zero-regression contract
 * ---------------------------------------------------------------------
 * The legacy arrays identify their items in four incompatible ways, and those
 * ids are frozen inside data we must never break:
 *   · colors / gola      → ARRAY INDEX      ("carc-3", optAllows(u,"carcass",3))
 *   · groups             → the number g.g   ("pg4", "pg4-c2")
 *   · pmat               → key string       ("aluminum", else "plinth-K")
 *   · handles/equipment  → code             · worktops/backsplash → name
 * Those ids live in every saved placement.config, in every u.opts allow-list,
 * in catalog_rules.py's validator and in the parity-locked JS≡PY pricing pair.
 * Renumbering ANY of them would silently reprice or mis-render stored quotes.
 *
 * So each entity keeps `legacy.raw` — the ORIGINAL item object, untouched —
 * and projection emits `{...raw, ...mapped(entity)}`. With no edits the output
 * is byte-identical to the input (pinned by the round-trip test); with edits,
 * only the mapped fields change and the item KEEPS ITS SLOT, its index, its
 * key/code/name. Legacy readers never learn the registry exists.
 *
 * Pure module: no React, no store, no I/O.
 */

export const SCHEMA_LIB = 1;

/* ── class specs — the ONE description of where a class lives in the blob and
 * which entity fields map back onto the legacy item. `arr(blob)` returns the
 * live array; `idOf(item, index)` reproduces the legacy identity exactly. ── */
const EQ_CLASSES = [
  ["sinks", "Sink"], ["taps", "Tap"], ["hobs", "Hob"], ["ovens", "Oven"],
  ["hoods", "Hood"], ["fridges", "Fridge"], ["dishwashers", "Dishwasher"],
  ["bins", "Waste bin"], ["corners", "Corner mechanism"], ["pullouts", "Pull-out"],
  ["columns", "Column"], ["organizers", "Drawer organizer"], ["hinges", "Hinge"],
  ["drawers", "Drawer system"], ["shelves", "Shelf"],
];

/** map entries: [entityPath, legacyField] — both directions use this list. */
const MAP_NAME_PRICE = [["identity.name", "name"], ["price.sale", "price"]];

export const CLASS_SPECS = {
  carcassColor: {
    label: "Carcass colour", key: "colors", idField: "index",
    map: [["identity.name", "name"], ["material.swatch", "sw"], ["material.texture", "tex"], ["price.sale", "price"]],
  },
  frontGroup: {
    label: "Front group", key: "groups", idField: "g",
    map: [["identity.name", "name"], ["price.sale", "price"], ["material.finish", "finish"], ["price.edgePerM", "edgePrice"]],
  },
  handle: {
    label: "Handle", key: "handles", idField: "code",
    map: [["identity.name", "name"], ["identity.code", "code"], ["price.sale", "price"], ["material.finish", "finish"], ["taxonomy.subcategory", "type"]],
  },
  gola: {
    label: "Gola", key: "gola", idField: "index",
    map: [["identity.name", "name"], ["material.swatch", "sw"], ["material.finish", "finish"], ["price.sale", "price"]],
  },
  plinthMaterial: {
    label: "Plinth material", key: "pmat", idField: "key",
    map: [["identity.name", "name"], ["identity.code", "key"], ["identity.description", "desc"], ["price.sale", "price"]],
  },
  worktop: {
    label: "Worktop", key: "worktops", idField: "name",
    map: [["identity.name", "name"], ["price.sale", "price"], ["dims.maxLenMm", "maxLenMm"]],
  },
  countertop: {
    label: "Countertop", key: "countertops", idField: "code",
    map: [["identity.name", "name"], ["identity.code", "code"], ["price.sale", "price"], ["material.finish", "material"], ["mfg.edge", "edge"]],
  },
  backsplash: { label: "Backsplash", key: "backsplash", idField: "name", map: MAP_NAME_PRICE },
  doorStyle: { label: "Door style", key: "dstyles", idField: "name", map: [["identity.name", "name"], ["identity.description", "desc"], ["price.addPerM2", "addM2"]] },
  appliance: { label: "Appliance (legacy)", key: "appliances", idField: "index", map: [["identity.name", "name"], ["identity.brand", "brand"], ["price.sale", "price"]] },
  component: { label: "Shared component", key: "compLib", idField: "id", map: [["identity.name", "name"], ["identity.code", "id"], ["media.model", "model"], ["taxonomy.subcategory", "role"]] },
};
EQ_CLASSES.forEach(function (pair) {
  CLASS_SPECS["equipment." + pair[0]] = {
    label: pair[1], key: "equipment." + pair[0], idField: "code",
    map: [
      ["identity.name", "model"], ["identity.code", "code"], ["identity.brand", "brand"],
      ["identity.description", "spec"], ["material.finish", "finish"], ["price.sale", "price"],
      ["media.thumb", "img"],
    ],
  };
});

export const LIB_CLASSES = Object.keys(CLASS_SPECS);

/* ── blob access for a class key ("colors" or "equipment.sinks") ─────────── */
function arrOf(blob, key) {
  if (key.indexOf("equipment.") === 0) {
    const cat = key.slice(10);
    const eq = blob.equipment || {};
    return Array.isArray(eq[cat]) ? eq[cat] : null;
  }
  return Array.isArray(blob[key]) ? blob[key] : null;
}
function setArr(blob, key, list) {
  if (key.indexOf("equipment.") === 0) {
    if (!blob.equipment) blob.equipment = {};
    blob.equipment[key.slice(10)] = list;
    return;
  }
  blob[key] = list;
}
export function legacyIdOf(spec, item, index) {
  if (spec.idField === "index") return index;
  if (spec.idField === "g") return item && item.g;
  return item ? item[spec.idField] : undefined;
}

/* ── tiny path helpers (entity fields are nested) ─────────────────────────── */
function getPath(o, path) {
  const parts = path.split(".");
  let cur = o;
  for (let i = 0; i < parts.length; i++) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[parts[i]];
  }
  return cur;
}
function setPath(o, path, v) {
  const parts = path.split(".");
  let cur = o;
  for (let i = 0; i < parts.length - 1; i++) {
    if (cur[parts[i]] == null || typeof cur[parts[i]] !== "object") cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = v;
}
function clearPath(o, path) {
  const parts = path.split(".");
  let cur = o;
  for (let i = 0; i < parts.length - 1; i++) {
    if (cur == null || typeof cur[parts[i]] !== "object" || cur[parts[i]] == null) return;
    cur = cur[parts[i]];
  }
  delete cur[parts[parts.length - 1]];
}
const clone = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)));

/* ── eid: stable, derived from class + legacy id so adoption is IDEMPOTENT
 * (re-running never mints a second entity for the same item). ───────────── */
export function makeEid(cls, legacyId) {
  const safe = String(legacyId == null ? "?" : legacyId)
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  return "ent_" + cls.replace(/[^a-zA-Z0-9]+/g, "_").toLowerCase() + "_" + (safe || "0");
}

/** The empty entity — every group present so editors can bind without guards. */
export function blankEntity(cls) {
  return {
    eid: "", cls: cls, legacy: null,
    identity: { name: "", sku: "", code: "", mpn: "", ean: "", brand: "", manufacturer: "", supplier: "", description: "" },
    taxonomy: { category: "", subcategory: "", family: "", tags: [] },
    lifecycle: { status: "active", version: 1, createdAt: "", updatedAt: "" },
    media: { thumb: "", images: [], model: "", lods: [], collision: "", preview: "" },
    geometry: { bboxMm: null, anchors: [], snaps: [], rotationOffsetDeg: 0, scale: 1, origin: "", materialSlots: [] },
    material: { swatch: "", texture: "", finish: "", pbr: null },
    dims: {},
    price: { currency: "EUR" },
    compat: { families: [], modules: { include: [], exclude: [] }, requires: [], conflicts: [], rules: [] },
    mfg: {},
    meta: { custom: {}, notes: "" },
  };
}

/* ── ADOPTION: legacy arrays → entities (idempotent, lossless) ───────────── */
export function adoptLegacy(blob, opts) {
  const now = (opts && opts.now) || "";
  if (!blob.entities) blob.entities = {};
  if (!blob.entityOrder) blob.entityOrder = {};
  LIB_CLASSES.forEach((cls) => {
    const spec = CLASS_SPECS[cls];
    const list = arrOf(blob, spec.key);
    if (!list) return;                                  // library absent → nothing to adopt
    const order = [];
    list.forEach((item, i) => {
      if (item == null || typeof item !== "object") { order.push(null); return; }
      const lid = legacyIdOf(spec, item, i);
      const eid = makeEid(cls, lid);
      const prev = blob.entities[eid];
      const e = prev || blankEntity(cls);
      e.eid = eid;
      e.cls = cls;
      // the compatibility binding — raw is the ORIGINAL item, never rewritten
      e.legacy = { key: spec.key, idField: spec.idField, id: lid, raw: clone(item) };
      // derive the editable fields from the item — a TRUE mirror both ways
      // (2026-08-18 fix: a field the item does NOT carry must be CLEARED on
      // the entity. Colour eids are INDEX-based, so a "+ New colour" at a
      // reused index used to inherit the DELETED colour's stale texture from
      // its old entity at projection time — the tile showed the old wood
      // photo and the colour picker stayed disabled: "I can't change the
      // color". Clearing undefined fields makes resurrection impossible.)
      spec.map.forEach((m) => {
        const v = item[m[1]];
        if (v !== undefined) setPath(e, m[0], clone(v));
        else clearPath(e, m[0]);
      });
      if (!e.lifecycle.createdAt) e.lifecycle.createdAt = now;
      e.lifecycle.updatedAt = now;
      if (!e.taxonomy.category) e.taxonomy.category = spec.label;
      blob.entities[eid] = e;
      order.push(eid);
    });
    blob.entityOrder[cls] = order;
  });
  blob.schemaLib = SCHEMA_LIB;
  blob.libAdoptV1 = true;
  return blob;
}

/* ── PROJECTION: entities → legacy arrays, slot-for-slot ───────────────────
 *
 * MEMBERSHIP IS THE ARRAY'S (2026-08-09 data-loss fix). This used to iterate
 * `entityOrder` and REBUILD the array from it, which made the registry decide
 * which items exist. Any item added after adoption (every "+ New handle",
 * "+ New colour", "+ New gola", "+ New material"…) was outside the order and
 * was therefore DELETED by the next projection, and any item removed was
 * RESURRECTED from its stale entity — silently, on the very same edit.
 *
 * The array is now the master for membership and order; the registry only
 * supplies EDITED FIELD VALUES for the slots it recognises. Length never
 * changes, unknown slots pass through untouched. */
export function projectLegacy(blob) {
  if (!blob || !blob.entities || !blob.entityOrder) return blob;
  LIB_CLASSES.forEach((cls) => {
    const spec = CLASS_SPECS[cls];
    if (!Array.isArray(blob.entityOrder[cls])) return;
    const cur = arrOf(blob, spec.key);
    if (!cur) return;
    const out = cur.map((item, i) => {
      if (item == null || typeof item !== "object") return clone(item);
      const e = blob.entities[makeEid(cls, legacyIdOf(spec, item, i))];
      // no entity for this slot (a brand-new item) → keep it exactly as authored
      if (!e || !e.legacy) return clone(item);
      const outItem = clone(item);
      spec.map.forEach((m) => {
        const v = getPath(e, m[0]);
        if (v === undefined) return;
        // never write an empty string over a field the raw item did not have:
        // that would add keys and break byte-identity for untouched libraries
        if (v === "" && outItem[m[1]] === undefined) return;
        outItem[m[1]] = clone(v);
      });
      return outItem;
    });
    setArr(blob, spec.key, out);
  });
  return blob;
}

/* ── entity lookup helpers ────────────────────────────────────────────────── */
export function entitiesOfClass(blob, cls) {
  const order = (blob && blob.entityOrder && blob.entityOrder[cls]) || [];
  return order.map((eid) => (eid ? (blob.entities || {})[eid] : null)).filter(Boolean);
}
export function allEntities(blob) {
  const m = (blob && blob.entities) || {};
  return Object.keys(m).map((k) => m[k]);
}
/** The legacy id an entity projects to — what allow-lists and configs store. */
export function entityLegacyId(e) { return e && e.legacy ? e.legacy.id : undefined; }

/* ── ASSEMBLY LOOKUP (Phase 2/4) — find the entity behind a legacy selection
 * and, when it owns one, ITS OWN GLB. This is what lets a module stop being a
 * single baked model: the carcass, the front, the handle, the shelf and every
 * accessory can each contribute their own geometry, and swapping one changes
 * only that component. Returns null (never a guess) when the entity has no
 * model of its own — the caller then keeps whatever it rendered before. */
export function findEntity(blob, cls, legacyId) {
  const order = (blob && blob.entityOrder && blob.entityOrder[cls]) || [];
  for (let i = 0; i < order.length; i++) {
    const e = order[i] ? (blob.entities || {})[order[i]] : null;
    if (e && e.legacy && String(e.legacy.id) === String(legacyId)) return e;
  }
  return null;
}
export function entityModel(blob, cls, legacyId) {
  const e = findEntity(blob, cls, legacyId);
  return (e && e.media && e.media.model) || null;
}
/** The engine render role an entity class contributes geometry to, or null
 *  when the class has no anchor in the cabinet assembly. */
export function assemblyRoleOf(cls) {
  if (cls === "carcassColor") return "carcass";
  if (cls === "frontGroup") return "front";
  if (cls === "handle") return "handle";
  if (cls === "plinthMaterial") return "plinth";
  if (cls === "worktop") return "worktop";
  if (cls === "equipment.drawers") return "drawer";
  return null;
}

/* ── DEPENDENCY GRAPH: where is this entity actually used? ────────────────── */
export function entityUsage(blob, eid, designs) {
  const e = (blob.entities || {})[eid];
  const out = { modules: [], projects: [], total: 0 };
  if (!e || !e.legacy) return out;
  const spec = CLASS_SPECS[e.cls];
  const optCat = legacyOptCategory(e.cls);
  const lid = e.legacy.id;
  (blob.carcass || []).forEach((u) => {
    if (!u) return;
    let hit = false;
    const opts = u.opts || {};
    if (optCat && Array.isArray(opts[optCat]) && opts[optCat].indexOf(lid) >= 0) hit = true;
    const cfg = u.cfg || {};
    if (!hit && spec && spec.idField === "index") {
      // index-encoded classes are referenced positionally by the unit config
      const fields = CFG_FIELDS_BY_CLASS[e.cls] || [];
      fields.forEach((f) => { if (cfg[f] === lid) hit = true; });
    }
    if (hit) out.modules.push(u.sku);
  });
  (designs || []).forEach((d) => {
    const pls = ((d && d.placements) || []);
    const used = pls.some((p) => {
      const c = (p && p.config) || {};
      return Object.keys(c).some((k) => String(c[k]) === String(lid) || (Array.isArray(c[k]) && c[k].some((x) => x && String(x.code) === String(lid))));
    });
    if (used) out.projects.push(d.id || d.projectId || "?");
  });
  out.total = out.modules.length + out.projects.length;
  return out;
}
/** the u.opts allow-list category a class is gated by (null = not gated) */
export function legacyOptCategory(cls) {
  if (cls === "carcassColor") return "carcass";
  if (cls === "frontGroup") return "groups";
  if (cls === "handle") return "handles";
  if (cls === "gola") return "gola";
  if (cls === "plinthMaterial") return "plinth";
  if (cls === "worktop") return "worktops";
  if (cls.indexOf("equipment.") === 0) return cls.slice(10);
  return null;
}
const CFG_FIELDS_BY_CLASS = {
  carcassColor: ["carc"],
  gola: ["gola"],
};

/* ── VALIDATION (Phase 13) — refuse to save a broken entity ──────────────── */
export function validateEntity(blob, e, opts) {
  const req = (opts && opts.require) || {};
  const errs = [];
  if (!e || !e.eid) return ["entity has no id"];
  const nm = (e.identity && e.identity.name) || "";
  if (!String(nm).trim()) errs.push("Display name is required");
  // duplicate SKU / internal code within the whole registry
  const sku = String((e.identity && e.identity.sku) || "").trim();
  const code = String((e.identity && e.identity.code) || "").trim();
  allEntities(blob).forEach((o) => {
    if (!o || o.eid === e.eid) return;
    if (sku && String((o.identity && o.identity.sku) || "").trim() === sku) errs.push("Duplicate SKU '" + sku + "' (also on " + (o.identity.name || o.eid) + ")");
    if (code && o.cls === e.cls && String((o.identity && o.identity.code) || "").trim() === code) errs.push("Duplicate code '" + code + "' in " + (CLASS_SPECS[e.cls] || {}).label);
  });
  // dimensions must be finite non-negative mm, and min<=max when both set
  const d = e.dims || {};
  Object.keys(d).forEach((k) => {
    const v = d[k];
    if (v == null || v === "") return;
    if (typeof v !== "number" || !isFinite(v) || v < 0) errs.push("Dimension " + k + " must be a positive number of millimetres");
  });
  if (typeof d.minMm === "number" && typeof d.maxMm === "number" && d.minMm > d.maxMm) errs.push("Minimum exceeds maximum");
  // prices must be finite non-negative
  const p = e.price || {};
  ["cost", "purchase", "production", "assembly", "transport", "sale"].forEach((k) => {
    const v = p[k];
    if (v == null || v === "") return;
    if (typeof v !== "number" || !isFinite(v) || v < 0) errs.push("Price field " + k + " must be a positive number");
  });
  // opt-in strictness (used by the "ready for production" gate, never on load)
  if (req.model && !(e.media && e.media.model)) errs.push("A 3D model (GLB) is required");
  if (req.thumb && !(e.media && (e.media.thumb || e.media.preview))) errs.push("A thumbnail is required");
  if (req.price && !(e.price && e.price.sale > 0)) errs.push("A sale price is required");
  // broken references
  (((e.compat || {}).requires) || []).forEach((r) => {
    if (r && r.eid && !(blob.entities || {})[r.eid]) errs.push("Requires a missing entity (" + r.eid + ")");
  });
  (((e.compat || {}).conflicts) || []).forEach((r) => {
    if (r && r.eid && !(blob.entities || {})[r.eid]) errs.push("Conflicts with a missing entity (" + r.eid + ")");
  });
  // circular dependency through requires
  if (hasCycle(blob, e.eid)) errs.push("Circular dependency in the requires chain");
  return errs;
}
function hasCycle(blob, startEid) {
  const seen = {};
  const stack = [startEid];
  let first = true;
  while (stack.length) {
    const id = stack.pop();
    if (!first && id === startEid) return true;
    first = false;
    if (seen[id]) continue;
    seen[id] = 1;
    const e = (blob.entities || {})[id];
    (((e || {}).compat || {}).requires || []).forEach((r) => { if (r && r.eid) stack.push(r.eid); });
  }
  return false;
}

/* ── COST ENGINE (Phase 9) — the sale price an entity resolves to. Explicit
 * sale wins; otherwise it is BUILT from the cost parts the technician entered.
 * Returns null when there is nothing to compute from (never a guessed 0). ── */
export function entitySalePrice(e) {
  const p = (e && e.price) || {};
  if (typeof p.sale === "number" && isFinite(p.sale)) return round2(p.sale);
  const parts = ["purchase", "production", "assembly", "transport"]
    .map((k) => (typeof p[k] === "number" && isFinite(p[k]) ? p[k] : 0));
  const base = parts.reduce((s, v) => s + v, 0);
  if (!base) return null;
  const waste = base * (num(p.wasteFactorPct) / 100);
  const withWaste = base + waste;
  const margin = withWaste * (num(p.marginPct) / 100);
  const beforeDisc = withWaste + margin;
  const disc = beforeDisc * (num(p.discountPct) / 100);
  return round2(beforeDisc - disc);
}
const num = (v) => (typeof v === "number" && isFinite(v) ? v : 0);
const round2 = (n) => Math.round(n * 100) / 100;
